import { useState, useEffect, useRef, useCallback } from 'react'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import { Link2, ExternalLink, AlertTriangle } from 'lucide-react'
import {
  apiListCampaignSuppliers,
  apiAddSupplier,
  apiImportSuppliers,
  apiBulkAddFromCatalog,
  apiSearchCatalogByEmail,
  type CampaignSupplier,
  type CampaignStatus,
  type CatalogAssignmentResult,
  type ImportSuppliersResult,
} from '../lib/api'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Skeleton } from './ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table'
import { CatalogPickerSection } from './CatalogSupplierPicker'

// ─── Props ────────────────────────────────────────────────────────────────────

type CampaignSupplierDialogProps = {
  campaignId: string
  campaignName: string
  /** Story 3.8 — statut campagne. Si 'ACTIVE', la modale affiche un bandeau
   * d'avertissement « envoi email immédiat » et les toasts post-ajout reflètent
   * le résultat de l'invitation (succès / échec d'email). */
  campaignStatus: CampaignStatus
  open: boolean
  onClose: (changed?: boolean) => void
}

// ─── Composant ────────────────────────────────────────────────────────────────

export function CampaignSupplierDialog({
  campaignId,
  campaignName,
  campaignStatus,
  open,
  onClose,
}: CampaignSupplierDialogProps) {
  const isActiveCampaign = campaignStatus === 'ACTIVE'
  const changedRef = useRef(false)
  // ─── État liste fournisseurs ────────────────────────────────────────────────
  const [suppliers, setSuppliers] = useState<CampaignSupplier[]>([])
  const [loadingList, setLoadingList] = useState(true)
  // Story 3.5 — la sélection catalogue (excludeCampaignId) doit être rafraîchie
  // après chaque ajout (manuel, CSV, catalogue) pour que les fournisseurs déjà
  // pris ne réapparaissent pas dans le picker.
  const [pickerVersion, setPickerVersion] = useState(0)

  // ─── État ajout manuel ─────────────────────────────────────────────────────
  const [manualName, setManualName] = useState('')
  const [manualEmail, setManualEmail] = useState('')
  const [nameError, setNameError] = useState('')
  const [emailError, setEmailError] = useState('')
  const [addingManual, setAddingManual] = useState(false)
  const [emailMatch, setEmailMatch] = useState<{ id: string; name: string; email: string } | null>(null)
  const [emailMatchLoading, setEmailMatchLoading] = useState(false)

  // ─── État import CSV ────────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [importingCsv, setImportingCsv] = useState(false)

  // ─── État ajout catalogue ───────────────────────────────────────────────────
  const [addingFromCatalog, setAddingFromCatalog] = useState(false)

  // ─── Chargement de la liste ─────────────────────────────────────────────────
  const reloadList = useCallback(async () => {
    setLoadingList(true)
    try {
      const data = await apiListCampaignSuppliers(campaignId)
      setSuppliers(data)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erreur lors du chargement des fournisseurs'
      toast.error(msg, { duration: Infinity })
    } finally {
      setLoadingList(false)
    }
  }, [campaignId])

  useEffect(() => {
    if (!open) {
      changedRef.current = false
      setManualName('')
      setManualEmail('')
      setNameError('')
      setEmailError('')
      setEmailMatch(null)
      return
    }
    reloadList()
  }, [open, reloadList])

  // ─── Validation ─────────────────────────────────────────────────────────────
  function validateName(value: string) {
    if (!value.trim()) {
      setNameError('Le nom est requis')
      return false
    }
    setNameError('')
    return true
  }

  function validateEmail(value: string) {
    if (!value.trim()) {
      setEmailError("L'adresse email est requise")
      return false
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(value)) {
      setEmailError("Format d'email invalide")
      return false
    }
    setEmailError('')
    return true
  }

  // Story 3.5 — bandeau dédoublonnage : sur blur de l'email, lookup catalogue.
  async function handleEmailBlur() {
    validateEmail(manualEmail)
    const trimmed = manualEmail.trim().toLowerCase()
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(trimmed)) {
      setEmailMatch(null)
      return
    }
    setEmailMatchLoading(true)
    try {
      const res = await apiSearchCatalogByEmail(trimmed)
      setEmailMatch(res.match)
    } catch {
      // Lookup silencieux : si ça échoue, on laisse Marie saisir librement.
      setEmailMatch(null)
    } finally {
      setEmailMatchLoading(false)
    }
  }

  // ─── Ajout manuel ──────────────────────────────────────────────────────────
  async function handleAddManual(e: React.FormEvent) {
    e.preventDefault()
    const nameOk = validateName(manualName)
    const emailOk = validateEmail(manualEmail)
    if (!nameOk || !emailOk) return

    setAddingManual(true)
    try {
      const newSupplier = await apiAddSupplier(campaignId, {
        name: manualName.trim(),
        email: manualEmail.trim(),
      })
      changedRef.current = true
      setSuppliers(prev => [...prev, newSupplier])
      setManualName('')
      setManualEmail('')
      setEmailMatch(null)
      setPickerVersion(v => v + 1)
      // Story 3.8 — sur campagne ACTIVE, l'invitation est envoyée immédiatement :
      // adapter le toast selon le résultat (succès / échec d'email).
      if (isActiveCampaign && newSupplier.emailFailures && newSupplier.emailFailures > 0) {
        toast.warning(
          'Fournisseur ajouté, invitation non envoyée — vous pouvez la relancer manuellement depuis la liste.',
          { duration: 8000 }
        )
      } else if (isActiveCampaign) {
        toast.success('Fournisseur ajouté et invité par email', { duration: 4000 })
      } else {
        toast.success('Fournisseur ajouté', { duration: 4000 })
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Erreur lors de l'ajout du fournisseur"
      toast.error(msg, { duration: Infinity })
    } finally {
      setAddingManual(false)
    }
  }

  // Story 3.5 — « Utiliser ce fournisseur » : ajoute directement depuis le catalogue
  async function handleUseCatalogMatch() {
    if (!emailMatch) return
    setAddingManual(true)
    try {
      const result = await apiBulkAddFromCatalog(campaignId, [emailMatch.id])
      showAssignmentToast(result, isActiveCampaign)
      changedRef.current = true
      await reloadList()
      setManualName('')
      setManualEmail('')
      setEmailMatch(null)
      setPickerVersion(v => v + 1)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Erreur lors de l'ajout depuis le catalogue"
      toast.error(msg, { duration: Infinity })
    } finally {
      setAddingManual(false)
    }
  }

  // ─── Ajout bulk catalogue ──────────────────────────────────────────────────
  async function handleAddFromCatalog(supplierIds: string[]) {
    setAddingFromCatalog(true)
    try {
      const result = await apiBulkAddFromCatalog(campaignId, supplierIds)
      showAssignmentToast(result, isActiveCampaign)
      changedRef.current = true
      await reloadList()
      setPickerVersion(v => v + 1)
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Erreur lors de l\'ajout depuis le catalogue'
      toast.error(msg, { duration: Infinity })
    } finally {
      setAddingFromCatalog(false)
    }
  }

  // ─── Import CSV ────────────────────────────────────────────────────────────
  async function handleImportCsv() {
    const file = fileInputRef.current?.files?.[0]
    if (!file) {
      toast.error('Veuillez sélectionner un fichier CSV', { duration: Infinity })
      return
    }

    setImportingCsv(true)
    try {
      const result = await apiImportSuppliers(campaignId, file)
      showImportToast(result, isActiveCampaign)
      changedRef.current = true
      await reloadList()
      setPickerVersion(v => v + 1)
      // Réinitialiser l'input fichier
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Erreur lors de l'import CSV"
      toast.error(msg, { duration: Infinity })
    } finally {
      setImportingCsv(false)
    }
  }

  // ─── Rendu ─────────────────────────────────────────────────────────────────
  return (
    <Dialog open={open} onOpenChange={open => !open && onClose(changedRef.current)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Fournisseurs — {campaignName}</DialogTitle>
        </DialogHeader>

        {/* Story 3.8 — Bandeau d'avertissement sur campagne ACTIVE :
            l'ajout déclenche un email d'invitation immédiat. */}
        {isActiveCampaign && (
          <div
            role="alert"
            className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-3"
          >
            <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" aria-hidden />
            <p className="text-sm text-amber-900">
              ⚠️ Vous ajoutez un fournisseur à une campagne en cours. Il recevra <strong>immédiatement</strong>{' '}
              un email d'invitation et sera intégré aux relances ultérieures.
            </p>
          </div>
        )}

        {/* ── Depuis le catalogue (Story 3.5 — Volet A) ────────────────── */}
        <section className="space-y-3" aria-labelledby="catalog-section-title">
          <h3 id="catalog-section-title" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Link2 className="h-4 w-4" aria-hidden />
            Depuis votre catalogue
          </h3>
          <CatalogPickerSection
            key={`picker-${pickerVersion}`}
            excludeCampaignId={campaignId}
            busy={addingFromCatalog}
            onConfirm={handleAddFromCatalog}
            emptyCatalogAction={
              <a
                href="/?view=suppliers"
                className="text-sm text-primary inline-flex items-center gap-1 hover:underline"
              >
                Créer un fournisseur dans le catalogue
                <ExternalLink className="h-3 w-3" />
              </a>
            }
          />
        </section>

        <div className="border-t my-2" />

        {/* ── Ajout manuel ─────────────────────────────────────────────── */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">Ajout manuel</h3>
          <form onSubmit={handleAddManual} className="flex flex-wrap gap-3 items-start">
            <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
              <Label htmlFor="supplier-name">Nom *</Label>
              <Input
                id="supplier-name"
                placeholder="Nom du fournisseur"
                value={manualName}
                onChange={e => {
                  setManualName(e.target.value)
                  if (nameError) setNameError('')
                }}
                onBlur={() => validateName(manualName)}
                disabled={addingManual}
              />
              {nameError && <p className="text-xs text-red-500">{nameError}</p>}
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
              <Label htmlFor="supplier-email">Email *</Label>
              <Input
                id="supplier-email"
                type="email"
                placeholder="email@fournisseur.com"
                value={manualEmail}
                onChange={e => {
                  setManualEmail(e.target.value)
                  if (emailError) setEmailError('')
                  if (emailMatch) setEmailMatch(null)
                }}
                onBlur={handleEmailBlur}
                disabled={addingManual}
              />
              {emailError && <p className="text-xs text-red-500">{emailError}</p>}
              {emailMatchLoading && (
                <p className="text-xs text-muted-foreground">Vérification dans votre catalogue...</p>
              )}
            </div>
            <div className="flex flex-col justify-end pt-6">
              <Button type="submit" disabled={addingManual}>
                {addingManual ? 'Ajout...' : 'Ajouter'}
              </Button>
            </div>
          </form>
          {/* Story 3.5 — bandeau dédoublonnage (AC #9) */}
          {emailMatch && (
            <div
              role="alert"
              className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-3 flex-wrap"
            >
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm text-amber-900">
                  Un fournisseur avec cet email existe dans votre catalogue : <strong>{emailMatch.name}</strong>.
                </p>
                <p className="text-xs text-amber-800 mt-1">
                  Conseil : utilisez-le plutôt que d'en créer un nouveau pour éviter les doublons.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="default"
                  onClick={handleUseCatalogMatch}
                  disabled={addingManual}
                >
                  Utiliser ce fournisseur
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setEmailMatch(null)}
                  disabled={addingManual}
                >
                  Continuer en saisie libre
                </Button>
              </div>
            </div>
          )}
        </section>

        <div className="border-t my-2" />

        {/* ── Import CSV ────────────────────────────────────────────────── */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700">Import CSV</h3>
          <p className="text-xs text-gray-500">
            Colonnes attendues : <code>nom, email, siret (optionnel), référence interne (optionnel)</code>
          </p>
          <div className="flex flex-wrap gap-3 items-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border file:border-gray-300 file:text-sm file:bg-white file:cursor-pointer"
              disabled={importingCsv}
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleImportCsv}
              disabled={importingCsv}
            >
              {importingCsv ? 'Import en cours...' : 'Importer le fichier'}
            </Button>
          </div>
        </section>

        <div className="border-t my-2" />

        {/* ── Liste des fournisseurs ────────────────────────────────────── */}
        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-700">
            Fournisseurs ({loadingList ? '…' : suppliers.length})
          </h3>

          {loadingList ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-9 w-full rounded" />
              ))}
            </div>
          ) : suppliers.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">
              Aucun fournisseur pour l'instant
            </p>
          ) : (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>SIRET</TableHead>
                    <TableHead>Référence interne</TableHead>
                    <TableHead>Lien catalogue</TableHead>
                    <TableHead>Date d'ajout</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suppliers.map(s => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="text-gray-600">{s.email}</TableCell>
                      <TableCell className="text-gray-500">{s.siret ?? '—'}</TableCell>
                      <TableCell className="text-gray-500">{s.internalRef ?? '—'}</TableCell>
                      <TableCell>
                        {s.catalogSupplierId ? (
                          <span
                            title="Lié au catalogue — actions cross-campagne disponibles"
                            className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
                          >
                            <Link2 className="h-3 w-3" />
                            Catalogue
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">Non lié</span>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-500 text-xs whitespace-nowrap">
                        {format(new Date(s.assignedAt), 'dd MMM yyyy', { locale: fr })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </DialogContent>
    </Dialog>
  )
}

function showAssignmentToast(result: CatalogAssignmentResult, isActiveCampaign: boolean) {
  const { added, skipped, emailFailures = 0 } = result
  if (added === 0 && skipped.length === 0) return

  // Story 3.8 — sur campagne ACTIVE, on superpose le résultat de l'invitation
  // par email (succès / échec partiel). `emailFailures > 0` ⇒ toast warning
  // qui invite à relancer manuellement depuis la liste.
  if (isActiveCampaign && added > 0 && emailFailures > 0) {
    toast.warning(
      `${added} fournisseur${added > 1 ? 's' : ''} ajouté${added > 1 ? 's' : ''}, ${emailFailures} invitation${emailFailures > 1 ? 's' : ''} non envoyée${emailFailures > 1 ? 's' : ''} — vous pouvez la relancer manuellement depuis la liste.`,
      { duration: 8000 }
    )
    return
  }
  if (isActiveCampaign && added > 0) {
    const skipMsg = skipped.length > 0
      ? `, ${skipped.length} ignoré${skipped.length > 1 ? 's' : ''} (déjà présent ou indisponible)`
      : ''
    toast.success(
      `${added} fournisseur${added > 1 ? 's' : ''} ajouté${added > 1 ? 's' : ''} et invité${added > 1 ? 's' : ''} par email${skipMsg}`,
      { duration: 4000 }
    )
    return
  }

  if (added > 0 && skipped.length === 0) {
    toast.success(
      `${added} fournisseur${added > 1 ? 's' : ''} ajouté${added > 1 ? 's' : ''} depuis votre catalogue`,
      { duration: 4000 }
    )
    return
  }
  if (added > 0 && skipped.length > 0) {
    toast.warning(
      `${added} fournisseur${added > 1 ? 's' : ''} ajouté${added > 1 ? 's' : ''}, ${skipped.length} ignoré${skipped.length > 1 ? 's' : ''} (déjà présent ou indisponible).`,
      { duration: 6000 }
    )
    return
  }
  toast.error(
    `Aucun fournisseur ajouté — ${skipped.length} déjà présent${skipped.length > 1 ? 's' : ''} ou indisponible${skipped.length > 1 ? 's' : ''}.`,
    { duration: 6000 }
  )
}

function showImportToast(result: ImportSuppliersResult, isActiveCampaign: boolean) {
  const { added, skipped, invalidRows, emailFailures = 0 } = result

  // Story 3.8 — campagne ACTIVE avec invitations partiellement échouées : warning explicite.
  if (isActiveCampaign && added > 0 && emailFailures > 0) {
    const skipMsg = skipped > 0 ? `, ${skipped} ignoré${skipped > 1 ? 's' : ''} (email déjà présent)` : ''
    const invalidMsg = invalidRows > 0 ? `, ${invalidRows} ligne${invalidRows > 1 ? 's' : ''} invalide${invalidRows > 1 ? 's' : ''}` : ''
    toast.warning(
      `${added} fournisseur${added > 1 ? 's' : ''} ajouté${added > 1 ? 's' : ''}${skipMsg}${invalidMsg}, ${emailFailures} invitation${emailFailures > 1 ? 's' : ''} non envoyée${emailFailures > 1 ? 's' : ''} — vous pouvez la relancer manuellement depuis la liste.`,
      { duration: 8000 }
    )
    return
  }

  const suffix =
    (skipped > 0 ? `, ${skipped} ignoré${skipped > 1 ? 's' : ''} (email déjà présent)` : '') +
    (invalidRows > 0 ? `, ${invalidRows} ligne${invalidRows > 1 ? 's' : ''} ignorée${invalidRows > 1 ? 's' : ''} (email invalide)` : '')
  const invitedSuffix = isActiveCampaign && added > 0 ? ' et invité' + (added > 1 ? 's' : '') + ' par email' : ''
  toast.success(
    `${added} fournisseur${added !== 1 ? 's' : ''} ajouté${added !== 1 ? 's' : ''}${invitedSuffix}${suffix}`,
    { duration: 4000 }
  )
}
